<template>
  <div class="relative inline-block text-left w-full" ref="root">
    <button
      type="button"
      :class="[
        'inline-flex w-full h-10 justify-between items-center rounded-xl px-4 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 transition-colors',
        themeClasses.button
      ]"
      @click="toggle"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-haspopup="'listbox'"
    >
      <span class="flex-1 flex items-center justify-between min-w-0">
        <span class="truncate">{{ buttonLeftLabel }}</span>
        <span v-if="suffixRight && displaySuffix" class="ml-3 text-gray-500">{{ displaySuffix }}</span>
      </span>
      <svg :class="['ml-2 h-4 w-4', themeClasses.icon]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div v-if="open" class="absolute z-[9999] mt-1 min-w-full w-max bg-white border border-gray-200 rounded-lg shadow-lg">
      <ul role="listbox" class="max-h-60 overflow-auto py-1 text-sm whitespace-nowrap">
        <li
          v-for="(opt, idx) in normalizedOptions"
          :key="opt.value + '-' + idx"
          role="option"
          :aria-selected="isSelected(opt.value) ? 'true' : 'false'"
          :class="[
            'px-3 py-2 cursor-pointer flex items-center justify-between text-gray-900',
            themeClasses.hover
          ]"
          @click="select(opt.value)"
        >
          <span class="flex-1 flex items-center justify-between whitespace-nowrap">
            <span>{{ optionLeftLabel(opt) }}</span>
            <span v-if="suffixRight" class="ml-3 text-gray-500">{{ optionRightLabel(opt) }}</span>
          </span>
          <span v-if="isSelected(opt.value)" :class="['ml-2', themeClasses.checkmark]">✔</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StandardDropdown',
  props: {
    modelValue: {
      type: [String, Number, Boolean, Object, null],
      default: null
    },
    options: {
      type: Array,
      required: false,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Select'
    },
    displaySuffix: {
      type: String,
      default: ''
    },
    suffixRight: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'blue',
      validator: (value) => ['blue', 'purple'].includes(value)
    }
  },
  data() {
    return { open: false }
  },
  computed: {
    themeClasses() {
      const themes = {
        blue: {
          button: 'bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100 focus:ring-blue-500',
          icon: 'text-blue-600',
          hover: 'hover:bg-blue-50',
          checkmark: 'text-blue-600'
        },
        purple: {
          button: 'bg-purple-50 text-purple-600 border border-purple-100 hover:bg-purple-100 focus:ring-purple-500',
          icon: 'text-purple-600',
          hover: 'hover:bg-purple-50',
          checkmark: 'text-purple-600'
        }
      }
      return themes[this.theme] || themes.blue
    },
    normalizedOptions() {
      if (!this.options || !Array.isArray(this.options)) {
        return []
      }
      return this.options.map(opt => {
        if (typeof opt === 'object' && opt !== null && 'value' in opt) {
          return {
            value: opt.value,
            label: String(opt.label != null ? opt.label : opt.value),
            right: 'right' in opt ? String(opt.right) : null
          }
        }
        return { value: opt, label: String(opt), right: null }
      })
    },
    selectedLabel() {
      const found = this.normalizedOptions.find(o => this.isEqual(o.value, this.modelValue))
      return found ? found.label : this.placeholder
    },
    buttonLeftLabel() {
      // When suffixRight, show left label only; otherwise include suffix appended
      if (this.suffixRight) return this.selectedLabel
      return this.selectedLabel + (this.displaySuffix || '')
    }
  },
  methods: {
    optionLeftLabel(opt) {
      return this.suffixRight ? opt.label : opt.label + (this.displaySuffix || '')
    },
    optionRightLabel(opt) {
      return opt.right != null ? opt.right : (this.displaySuffix || '')
    },
    toggle() { this.open = !this.open },
    close() { this.open = false },
    isEqual(a, b) {
      if (typeof a === 'object' || typeof b === 'object') {
        try { return JSON.stringify(a) === JSON.stringify(b) } catch (_) { return a === b }
      }
      return a === b
    },
    isSelected(value) { return this.isEqual(value, this.modelValue) },
    select(value) {
      const isNumberOption = this.normalizedOptions.some(o => typeof o.value === 'number')
      const emitted = isNumberOption ? Number(value) : value
      this.$emit('update:modelValue', emitted)
      this.$emit('change', emitted)
      this.close()
    },
    handleClickOutside(e) {
      const root = this.$refs.root
      if (root && !root.contains(e.target)) this.close()
    }
  },
  mounted() { document.addEventListener('click', this.handleClickOutside) },
  beforeUnmount() { document.removeEventListener('click', this.handleClickOutside) }
}
</script>

<style scoped>
</style>
